// {feed}/history.json, append-only (§14.5). Com P-09 (09/10), um seq novo pode repetir os números oficiais quando só
// o estado público da probabilidade mudou.
import * as z from 'zod/mini';
import {
  type CandidateNumber,
  CandidatePairSchema,
  type FeedHeader,
  feedHeaderShape,
  IntSchema,
  type Iso,
  IsoSchema,
  type Office,
  OfficeSchema,
  type PlaceCode,
  PlaceCodeSchema,
} from './base';
import { type ProbabilityStatus, ProbabilityStatusSchema, type Quantiles } from './probability';
import { type CountStatus, CountStatusSchema } from './tally';

export interface History extends FeedHeader<'results.history'> {
  office: Office;
  scope: 'BR' | PlaceCode;
  candidates: [CandidateNumber, CandidateNumber];
  /** Um por seq publicado, ordenado por seq. */
  points: HistoryPoint[];
}
/**
 * Leitura A do DR-31, ao vivo: o comparecimento das seções já totalizadas contra o das mesmas seções no 1º turno.
 * [R] rascunho a fechar com o SINAL do motor (se a noite o calcula, exato ou estimado, e se haverá por UF).
 */
export interface SameSections {
  /** Comparecimento / aptos das seções já totalizadas no 2º turno (0–1, 6 casas). */
  turnout_fraction: number;
  /** O mesmo, no 1º turno, nas mesmas seções. */
  turnout_fraction_1t: number;
  /** exact = lista de seções conhecida ou lugar completo; estimated = pelo modelo de ordem. */
  basis: 'exact' | 'estimated';
  /** Faixa de 90% de turnout_fraction_1t; só com basis 'estimated'. */
  band_1t?: [number, number];
}
export interface HistoryPoint {
  seq: number;
  tse_ht: Iso;
  /** = tMin(tse_ht) */
  t_min: number;
  /** 0–1, 6 casas. */
  sections_fraction: number;
  electorate_fraction: number;
  /** Na ordem de candidates. */
  votes: [number, number];
  valid_votes: number;
  status: CountStatus;
  probability?: {
    status: ProbabilityStatus;
    base_seq: number;
    win_prob_b: number;
    final_share_b: Pick<Quantiles, 'q10' | 'q50' | 'q90'>;
  } | null;
  /** Ausente ou null = sem a leitura neste ponto. [R] rascunho a fechar com o SINAL do motor. */
  same_sections?: SameSections | null;
}

/** [R] rascunho a fechar com o SINAL do motor. */
export const SameSectionsSchema = z.object({
  turnout_fraction: z.number(),
  turnout_fraction_1t: z.number(),
  basis: z.enum(['exact', 'estimated']),
  band_1t: z.optional(z.tuple([z.number(), z.number()])),
});

const historyPointShape = {
  seq: IntSchema,
  tse_ht: IsoSchema,
  t_min: z.number(),
  sections_fraction: z.number(),
  electorate_fraction: z.number(),
  votes: z.tuple([IntSchema, IntSchema]),
  valid_votes: IntSchema,
  status: CountStatusSchema,
  probability: z.optional(
    z.nullable(
      z.object({
        status: ProbabilityStatusSchema,
        base_seq: IntSchema,
        win_prob_b: z.number(),
        final_share_b: z.object({ q10: z.number(), q50: z.number(), q90: z.number() }),
      }),
    ),
  ),
};

/**
 * Schema de leitura do ponto (I-20). `same_sections` chega como veio e é conferido à parte em validateHistory: fora da
 * estrutura vira null e o ponto fica (um bloco de análise ainda em rascunho não tira número oficial da linha do
 * tempo). Sem z.catch, de propósito: este módulo está no caminho crítico do site ao vivo, e o z.catch levaria para o JS
 * crítico uma parte do zod que o site não usa (B01, §15.7).
 */
export const HistoryPointSchema = z.object({
  ...historyPointShape,
  same_sections: z.optional(z.unknown()),
});
/** Ponto estrito, só para o JSON Schema da engenharia: o que o publicador deve gravar. */
export const HistoryPointPublishedSchema = z.object({
  ...historyPointShape,
  same_sections: z.optional(z.nullable(SameSectionsSchema)),
});

/** Pontos validados um a um (I-20): o arquivo só exige a lista. */
export const HistorySchema = z.object({
  ...feedHeaderShape('results.history'),
  office: OfficeSchema,
  scope: z.union([z.literal('BR'), PlaceCodeSchema]),
  candidates: CandidatePairSchema,
  points: z.array(z.unknown()),
});
export const HistoryPublishedSchema = z.object({
  ...feedHeaderShape('results.history'),
  office: OfficeSchema,
  scope: z.union([z.literal('BR'), PlaceCodeSchema]),
  candidates: CandidatePairSchema,
  points: z.array(HistoryPointPublishedSchema),
});
